import type { ISeriesPrototype, ISeriesWithEvents } from "@/types/series.types";
import {
	t_loadSeriesListForChannel,
	t_loadSeriesFull,
	t_loadCurrentSeriesForProject,
	t_createSeries,
	t_addEventToSeries,
	t_setSeriesEvents,
	t_deleteEventFromSeries,
	t_clearCurrentSeries,
	t_clearSeriesList,
	t_appendSeriesSync,
	t_setCurrentSeriesSync,
	type SeriesActions
} from 'store/actions/admin/series';
import { handle } from "redux-pack";
import type { SelectedBrandliveEvent } from "@/types/working-model";
import { eventStart } from "@/store/utils/event-start";
import { eventEnd } from "@/store/utils/event-end";
import { withoutEvent } from "@/store/utils/without-event";

export type SeriesState = {
	seriesList: ISeriesPrototype[] | null;
	totalSeries: number;
	currentSeries: ISeriesWithEvents | null;
	loadingSeriesList: boolean;
	loadingCurrentSeries: boolean;
	seriesError: boolean;
	topDrawerOpen: boolean;
};

export const initialState: SeriesState = {
	seriesList: null,
	totalSeries: 0,
	currentSeries: null,
	loadingSeriesList: false,
	loadingCurrentSeries: false,
	seriesError: false,
	topDrawerOpen: false,
};

export default function SeriesReducer(state: SeriesState = initialState, action: SeriesActions): SeriesState {
	switch (action.type) {
		case t_loadSeriesListForChannel: {
			return handle(state, action, {
				start: state => {
					return {
						...state,
						loadingSeriesList: true
					};
				},
				success: state => {
					return {
						...state,
						seriesList: action.payload?.series ?? [],
						seriesError: false,
						totalSeries: action.payload?.total ?? 0
					};
				},
				failure: state => {
					return {
						...state,
						seriesError: true
					};
				},
				finish: state => {
					return {
						...state,
						loadingSeriesList: false
					};
				}
			});
		}
		case t_loadSeriesFull: {
			return handle(state, action, {
				start: state => {
					return {
						...state,
						loadingCurrentSeries: true
					};
				},
				success: state => {
					return {
						...state,
						currentSeries: action.payload || null,
						seriesError: false
					};
				},
				failure: state => {
					return {
						...state,
						seriesError: true,
					};
				},
				finish: state => {
					return {
						...state,
						loadingCurrentSeries: false
					};
				}
			});
		}
		case t_loadCurrentSeriesForProject: {
			return handle(state, action, {
				start: state => {
					return {
						...state,
						loadingCurrentSeries: true
					};
				},
				success: state => {
					return {
						...state,
						currentSeries: action.payload || null,
						seriesError: false
					};
				},
				failure: state => {
					return {
						...state,
						seriesError: true,
					};
				},
				finish: state => {
					return {
						...state,
						loadingCurrentSeries: false
					};
				}
			});
		}
		case t_createSeries: {
			return handle(state, action, {
				start: state => {
					return state;
				},
				success: state => {
					return action.payload ? {
						...state,
						seriesList: [...(state.seriesList ?? []), action.payload],
						seriesError: false
					} : state;
				},
				failure: state => {
					return {
						...state,
						seriesError: true
					};
				},
				finish: state => {
					return state;
				}
			});
		}
		case t_addEventToSeries: {
			return handle(state, action, {
				start: state => {
					return state;
				},
				success: state => {
					const proj = action.meta as SelectedBrandliveEvent;
					const seriesProject = {
						uuid: proj?.uuid || '',
						title: proj?.sessions[0].title ?? { base: '', changed: '' },
						timestamp: proj ? eventStart(proj) : 0,
						end_timestamp: proj ? eventEnd(proj) : 0,
						event_image: proj?.event_image,
					};
					return {
						...state,
						seriesError: false,
						currentSeries: state.currentSeries ? {
							...state.currentSeries,
							events: [...state.currentSeries.events, seriesProject],
						} : null,
						seriesList: state.seriesList?.map((ser) => {
							if (ser.events && ser.uuid === action.payload?.series_uuid) {
								ser.events = [...ser.events, seriesProject];
							}
							return ser;
						}) || null
					};
				},
				failure: state => {
					return {
						...state,
						seriesError: true
					};
				},
				finish: state => {
					return state;
				}
			});
		}
		case t_setSeriesEvents: {
			return handle(state, action, {
				start: state => {
					return state;
				},
				success: state => {
					let currentSeries = state.currentSeries;
					if (state.currentSeries?.uuid && state.currentSeries.uuid === action.meta?.seriesUuid && action.meta?.eventData) {
						currentSeries = {
							...state.currentSeries,
							events: action.meta.eventData,
						};
					}
					return {
						...state,
						seriesError: false,
						seriesList: state.seriesList?.map((ser) => {
							if (ser.uuid === action.meta?.seriesUuid && action.meta?.eventData) {
								ser.events = action.meta?.eventData;
							}
							return ser;
						}) || null,
						currentSeries
					};
				},
				failure: state => {
					return {
						...state,
						seriesError: true
					};
				},
				finish: state => {
					return state;
				}
			});
		}
		case t_deleteEventFromSeries: {
			return handle(state, action, {
				start: state => {
					return state;
				},
				success: state => {
					return {
						...state,
						currentSeries: (state.currentSeries && action.meta) ? {
							...withoutEvent(action.meta)(state.currentSeries) as ISeriesWithEvents
						} : state.currentSeries,
						seriesList: (state.seriesList && action.meta) ?
							state.seriesList.filter(withoutEvent(action.meta)) :
							state.seriesList,
						seriesError: false
					};
				},
				failure: state => {
					return {
						...state,
						seriesError: true
					};
				},
				finish: state => {
					return state;
				}
			});
		}
		case t_clearCurrentSeries: {
			return {
				...state,
				currentSeries: null
			};
		}
		case t_clearSeriesList: {
			return {
				...state,
				seriesList: null
			};
		}
		case t_appendSeriesSync: {
			return {
				...state,
				seriesList: state.seriesList ? [
					...state.seriesList,
					action.payload
				] : [action.payload]
			};
		}
		case t_setCurrentSeriesSync: {
			return {
				...state,
				currentSeries: action.payload
			};
		}
	}
	return state;
}